/* eslint-disable no-underscore-dangle */
/**
 * useMap Application Logic Hook
 * Controls the behaviour of common shared elements present in a map,
 * like popups, markers, location and so on.
 * To use this hook, ensure your leaflet markers has the JSX property markerId
 *
 * References:
 *  - https://github.com/stefanocudini/leaflet-list-markers
 *
 * Possible updates:
 *  - Make map load data after get user location, to upload
 *  - Save addresses in a useMap state
 */
import { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import publicIp from 'public-ip';
import ipLocation from 'iplocation';
import { findMarkerObject } from '../support/services/leaflet';
import { loadState, saveState } from '../support/services/localStorage';

export default function useMap() {
  const [map, setMap] = useState(null);
  const [loadingMap, setLoadingMap] = useState(true);
  const [userLocation, setUserLocation] = useState(null);
  const [popupOpened, setPopupOpened] = useState(false);
  const [openedProducerId, setOpenedProducer] = useState(null);
  const [urlLatLng, setUrlLocation] = useState(null);
  const [geoPermissionStatus, setGeoPermissionStatus] = useState('prompt');
  const mapFirstOpened = useRef(true);
  const location = useLocation();

  const STANDARD_ZOOM = 16;
  /**
   * Rise the map zoom
   */
  const zoomIn = () => map.zoomIn();

  /**
   * Decrease the map zoom
   */
  const zoomOut = () => map.zoomOut();

  /**
   * Try to find use location an triggers the map event 'locationfound'
   */
  const locateUser = () => map.locate();

  /**
   * Move to location zooming and close any opened popup
   * @param {Latlng} latlng given coords
   */
  const moveToLocation = (latlng, options = {}) => {
    const { fast = false, zoom = STANDARD_ZOOM } = options;

    if (map._popup) map._popup._close();

    if (fast) map.setView(latlng, zoom);
    else map.flyTo(latlng, zoom);
  };

  /**
   * Open popup by marker id
   * Because of a 'bug' that still unsolved, if your marker is not been displayed in the screen,
   * you need to call modeToLocation before openPopup
   * @param {String} markerId
   */
  const openPopup = (markerId) => {
    const selectedMarker = findMarkerObject(map, markerId);

    if (selectedMarker) selectedMarker.openPopup();
  };

  /**
   * Close popup by marker id
   * @param {String} markerId
   */
  const closePopup = (markerId) => {
    if (markerId) {
      const selectedMarker = findMarkerObject(map, markerId);
      selectedMarker.closePopup();
    } else if (map._popup) map._popup._close();
  };

  /**
   * Initialize leaflet map under hook
   * @param {LeafletMap} leafletMap
   */
  const initMap = (leafletMap) => {
    setMap(leafletMap);
  };

  const verifyWhereAndMove = (userLatLng) => {
    if (urlLatLng && mapFirstOpened.current) moveToLocation(urlLatLng, { fast: true });
    else moveToLocation(userLatLng, { fast: true });
    mapFirstOpened.current = false;
  };

  useEffect(() => {
    if (map) {
      map.on('popupopen', () => {
        setPopupOpened(map?._popup);
        setOpenedProducer(map?._popup?.options.producerId || openedProducerId);
      });

      map.on('popupclose', () => {
        setTimeout(() => setPopupOpened(map?._popup), 100);
      });

      map.on('locationfound', (e) => {
        setGeoPermissionStatus('granted');
        setUserLocation(e.latlng);
        verifyWhereAndMove(e.latlng);
      });

      map.on('locationerror', () => {
        navigator.permissions
          .query({ name: 'geolocation' }) // could be granted, prompt and denied
          .then(async (permissionStatus) => {
            setGeoPermissionStatus(permissionStatus.state);
            if (permissionStatus.state === 'denied') {
              const ip = await publicIp.v4();
              const location = await ipLocation(ip);
              setUserLocation({ lat: location.latitude, lng: location.longitude });
              verifyWhereAndMove([location.latitude, location.longitude]);
            } else ; // ...need to handle it

            permissionStatus.addEventListener('change', (e) => {
              setGeoPermissionStatus(e.target.state);
            });
          });
      });

      map.locate();
      setLoadingMap(false);
      return () => map.off();
    }

    return () => {};
  }, [map]);

  useEffect(() => {
    // only open user popup when map is created if !userHasAlreadyOpenedMa
    if (userLocation && map) {
      const persitedState = loadState();

      if (!persitedState?.AuthReducer?.userHasAlreadyOpenedMap) {
        openPopup('userMarker');
        saveState({
          ...persitedState,
          AuthReducer: {
            ...persitedState?.AuthReducer,
            userHasAlreadyOpenedMap: true,
          },
        });
      }
    }
  }, [userLocation]);

  useEffect(() => {
  // Check if is a direct entrance on map or thorugh a space
    const urlSplited = location.pathname.split('/');
    if (urlSplited[urlSplited.length - 1][0] === '@') {
      const latlngSplited = urlSplited[urlSplited.length - 1].split(',');
      setUrlLocation({
        lat: latlngSplited[0].substring(1),
        lng: latlngSplited[1],
      });
    }
  }, []);

  return {
    sharedStates: {
      userLocation,
      openedProducerId,
      popupOpened,
      geoPermissionStatus,
      loadingMap,
    },
    commands: {
      zoomIn,
      zoomOut,
      locateUser,
      initMap,
      closePopup,
      openPopup,
      moveToLocation,
    },
  };
}
